import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AlertDto } from '../shared/api';
import { ROLE_LABEL } from '../shared/domain';
import { ApiError, get, makeData, post, queryClient, useBootstrap, whileInUse } from './api';
import { ArticleCard, ItemCard } from './components/cards';
import { Ctx, Err, Spinner, errMsg, type AppCtx, type StartReq } from './components/ui';
import { Scan } from './pages/Scan';
import { Articles, Items, Projects, Shelves, Today } from './pages/Lists';
import { Activity, DailyReport, Users, Weekly } from './pages/Reports';
import { Kit } from './pages/Kit';
import { Import, Labels } from './pages/Tools';

const PAGES: [string, string][] = [
  ['/', 'Scan'],
  ['/today', 'Today'],
  ['/items', 'Items'],
  ['/articles', 'Articles'],
  ['/projects', 'Projects'],
  ['/shelves', 'Storage areas'],
  ['/kit', 'Installation suitcase'],
  ['/report', 'Daily report'],
  ['/activity', 'Activity log'],
  ['/weekly', 'Weekly report'],
  ['/users', 'Users'],
  ['/labels', 'Labels'],
  ['/import', 'Import sheet'],
];

export function App() {
  const boot = useBootstrap();
  if (boot.isLoading) return <Spinner />;
  if (boot.error instanceof ApiError && boot.error.status === 401) return <Login />;
  if (boot.error || !boot.data)
    return (
      <div className="login">
        <div className="panel">
          <h1 style={{ fontSize: 24 }}>The app cannot load</h1>
          <p className="meta">{errMsg(boot.error)}</p>
          <button className="btn primary" onClick={() => void boot.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  return <Shell />;
}

/** Set on a successful sign-in, so the shell shows the logo once. */
let splashAfterLogin = false;

function Splash({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className="splash" onClick={onDone} role="presentation">
      <img src="/logo.webp" alt="" />
      <b>Boghche</b>
      <span className="meta">PANTOhealth warehouse</span>
    </div>
  );
}

function Login() {
  const users = useQuery({ queryKey: ['login-users'], queryFn: () => get<{ id: number; name: string; role: 'owner' | 'user' }[]>('/api/login-users') });
  const [uid, setUid] = useState<number | null>(null);
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const us = users.data ?? [];
  const sel = uid ?? us[0]?.id ?? null;
  const login = async () => {
    if (!sel) return;
    setBusy(true);
    setErr('');
    try {
      await post('/api/login', { userId: sel, pin });
      splashAfterLogin = true;
      await queryClient.resetQueries();
    } catch (e) {
      setErr(errMsg(e));
      setPin('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login">
      <div className="brand login-brand">
        <img src="/logo.webp" alt="" className="logo big" />
        <span>
          Boghche<small>PANTOhealth warehouse</small>
        </span>
      </div>
      <div className="panel">
        <h1 style={{ fontSize: 26 }}>Sign in</h1>
        <p className="meta" style={{ margin: '0 0 14px' }}>
          Only listed users can use the app. Everything you do is saved with your name, the date and the time.
        </p>
        {users.isLoading ? (
          <Spinner />
        ) : !us.length ? (
          <p className="err">No users yet. Create the first warehouse owner on the server (see the README).</p>
        ) : (
          <>
            <label className="field">
              Name
              <select value={sel ?? ''} onChange={(e) => setUid(Number(e.target.value))}>
                {us.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({ROLE_LABEL[u.role]})
                  </option>
                ))}
              </select>
            </label>
            <label className="field" style={{ marginTop: 10 }}>
              PIN
              <input type="password" inputMode="numeric" autoComplete="current-password" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void login()} autoFocus />
            </label>
            <button className="btn primary" style={{ marginTop: 14, width: '100%' }} disabled={busy || !pin} onClick={() => void login()}>
              Sign in
            </button>
            <Err e={err} />
          </>
        )}
      </div>
    </div>
  );
}

function Shell() {
  const boot = useBootstrap();
  const d = useMemo(() => makeData(boot.data!), [boot.data]);
  const nav = useNavigate();
  const loc = useLocation();
  const [dialog, setDialog] = useState<{ kind: 'item'; no: string } | { kind: 'article'; id: number } | null>(null);
  const [toastMsg, setToastMsg] = useState('');
  const [pending, setPending] = useState<StartReq | null>(null);
  const [actUser, setActUser] = useState('');
  const [homeKey, setHomeKey] = useState(0);
  const [splash, setSplash] = useState(splashAfterLogin);
  const endSplash = useCallback(() => setSplash(false), []);
  useEffect(() => {
    splashAfterLogin = false;
  }, []);
  const toastT = useRef<ReturnType<typeof setTimeout>>(undefined);
  const alerts = useQuery({ queryKey: ['alerts'], queryFn: () => get<AlertDto[]>('/api/alerts'), refetchInterval: whileInUse(120_000) });
  const badCount = (alerts.data ?? []).filter((a) => a.bad).length;

  const toast = useCallback((m: string) => {
    setToastMsg(m);
    clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToastMsg(''), 2600);
  }, []);
  const go = useCallback(
    (p: string) => {
      setDialog(null);
      nav(p);
      window.scrollTo(0, 0);
    },
    [nav],
  );
  const ctx: AppCtx = {
    d,
    toast,
    openItem: (no) => setDialog({ kind: 'item', no }),
    openArticle: (id) => setDialog({ kind: 'article', id }),
    closeDialog: () => setDialog(null),
    go,
    startAction: (r) => {
      setPending(r);
      go('/');
    },
  };
  useEffect(() => setDialog(null), [loc.pathname]);

  const logout = async () => {
    await post('/api/logout').catch(() => undefined);
    await queryClient.resetQueries();
    nav('/');
  };

  return (
    <Ctx.Provider value={ctx}>
      <div className="app">
        <nav>
          <a
            className="brand"
            href="/"
            title="Home"
            onClick={(e) => {
              e.preventDefault();
              setHomeKey((k) => k + 1);
              go('/');
            }}
          >
            <img src="/logo.webp" alt="" className="logo" />
            <span>
              Boghche<small>PANTOhealth warehouse</small>
            </span>
          </a>
          {PAGES.map(([p, l]) => (
            <button key={p} className={loc.pathname === p ? 'on' : ''} onClick={() => go(p)}>
              <span>{l}</span>
              {p === '/today' && badCount > 0 && <span className="cnt">{badCount}</span>}
            </button>
          ))}
          <div className="who">
            <span className="meta">Signed in as</span>
            <b>{d.me.name}</b>
            <span className="meta">
              {ROLE_LABEL[d.me.role]}
              {d.b.checkerId === d.me.id ? ', Checker on duty' : ''}
            </span>
            <br />
            <button className="btn small" style={{ marginTop: 6 }} onClick={() => void logout()}>
              Sign out
            </button>
          </div>
        </nav>
        <main>
          <Routes>
            <Route path="/" element={<Scan key={`scan-${homeKey}`} pending={pending} clearPending={() => setPending(null)} />} />
            <Route path="/today" element={<Today />} />
            <Route path="/items" element={<Items />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/shelves" element={<Shelves />} />
            <Route path="/kit" element={<Kit />} />
            <Route path="/report" element={<DailyReport />} />
            <Route path="/activity" element={<Activity key={actUser} initialUser={actUser} />} />
            <Route path="/weekly" element={<Weekly />} />
            <Route
              path="/users"
              element={
                <Users
                  onOpenUser={(n) => {
                    setActUser(n);
                    go('/activity');
                  }}
                />
              }
            />
            <Route path="/labels" element={<Labels />} />
            <Route path="/import" element={<Import />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      {dialog?.kind === 'item' && <ItemCard key={dialog.no} no={dialog.no} />}
      {dialog?.kind === 'article' && <ArticleCard key={dialog.id} id={dialog.id} />}
      {splash && <Splash onDone={endSplash} />}
      <div className={`toast ${toastMsg ? 'on' : ''}`} role="status">
        {toastMsg}
      </div>
    </Ctx.Provider>
  );
}
